/**
 * `<ProDocUploadDialog>` — **upload as a popup over the current screen** (W22,
 * FR-59, plan D57): today's `<ProDocUploader>` — the drop area, the workspace,
 * folder and type, the rows of FR-55 — inside the kit's `Dialog` at its widest.
 * Every `<ProDocUploaderProps>` key passes through (`folderPath`,
 * `documentType`, `parse`, …), so a sibling opens the same upload over its own
 * screens (ADR-0010).
 *
 * **Closing stops nothing.** The batch is a module store (`upload-queue.ts`)
 * that outlives the popup: files still uploading or being read go on, the
 * `<ProDocUploadTray>` counts them in the corner and reopens the popup with
 * every row as it was. While the popup is open the tray steps aside, and a drop
 * anywhere on the window adds to the batch, as the upload page's did.
 *
 * - `initialFiles` — files dropped onto a screen before the popup opened, added
 *   once the organisation's upload settings are read (once per array).
 * - `workspaceId` / `folderId` — where it files by default: the place being
 *   browsed. The pickers still offer the others.
 * - `sheet` — full screen below the `sm` width (W25, FR-59's phone mode): the
 *   kit's `Dialog sheet` (fiveox #446) — no margin and no rounding on a phone,
 *   the dialog at its widest from `sm` up. Off unless the host asks.
 */
import { Dialog } from '@fiveox/ui'
import { useEffect } from 'react'

import { ProDocUploader, type ProDocUploaderProps } from './uploader'
import { noteHostOrg, setUploadDialogOpen } from './upload-queue'

export interface ProDocUploadDialogProps extends ProDocUploaderProps {
  open: boolean
  /** The kit's close — Escape, the ×, the backdrop. Closing stops no upload. */
  onClose: () => void
  /** Files dropped before the popup opened, added as it opens. */
  initialFiles?: File[]
  /** W25: full screen below the `sm` width — the kit's `Dialog sheet`. */
  sheet?: boolean
  /** The popup's title — *Upload documents* unless the host names it. */
  title?: string
}

export function ProDocUploadDialog({ open, onClose, sheet = false, title = 'Upload documents', ...uploader }: ProDocUploadDialogProps) {
  const { ownerApp, orgId } = uploader
  // While it is open the tray steps aside; closing marks what finished as seen.
  useEffect(() => {
    if (!open) return
    setUploadDialogOpen(ownerApp, true)
    return () => setUploadDialogOpen(ownerApp, false)
  }, [open, ownerApp])
  // An org switch the person went ahead with stops the previous organisation's files not yet sent (FR-59).
  useEffect(() => noteHostOrg(ownerApp, orgId), [ownerApp, orgId])

  return (
    <Dialog open={open} onClose={onClose} title={title} width="xl" sheet={sheet}>
      {open && <ProDocUploader {...uploader} />}
    </Dialog>
  )
}
